import {
    Box,
    Button,
    Flex,
    Grid,
    GridItem,
    Text,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    Input,
    InputGroup,
    InputLeftElement,
    useDisclosure,
    Select,
    useToast,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    VStack,
    HStack,
    Badge,
    Divider,
    Checkbox,
    FormControl,
    FormLabel,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Tooltip,
    IconButton,
    SimpleGrid,
} from "@chakra-ui/react";
import {
    AddIcon,
    EditIcon,
    DeleteIcon,
    LockIcon,
    TimeIcon,
    RepeatClockIcon,
    SearchIcon,
    InfoOutlineIcon,
    NotAllowedIcon,
    CheckIcon,
    UnlockIcon,
} from "@chakra-ui/icons";
import { useEffect, useMemo, useState } from "react";

const ROLE_META = {
    1: { label: "Visualização de Passagens", color: "blue", network: "Externo (restrito)", networkColor: "orange" },
    2: { label: "Lotes e Passagens", color: "purple", network: "Externo (restrito)", networkColor: "orange" },
    3: { label: "Validação", color: "cyan", network: "Externo (restrito)", networkColor: "orange" },
    4: { label: "Supervisão", color: "yellow", network: "Externo (restrito)", networkColor: "orange" },
    5: { label: "Administrador", color: "red", network: "Externo (completo)", networkColor: "green" },
};

const formatDateTime = (value) => {
    if (!value) return "—";
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString("pt-BR", {
        timeZone: "America/Sao_Paulo",
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatRelative = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const diff = Date.now() - date.getTime();
    if (diff < 0) return null;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "agora mesmo";
    if (minutes < 60) return `há ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `há ${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `há ${days}d`;
    const months = Math.floor(days / 30);
    if (months < 12) return `há ${months} meses`;
    return `há ${Math.floor(months / 12)} anos`;
};

const INITIAL_USER_STATE = {
    username: "",
    password: "",
    role: 1,
    external: false,
    externalFullAccess: false,
    isMachine: false,
    serviceHoursEnabled: false,
    serviceHoursStart: "07:00",
    serviceHoursEnd: "13:00",
    blockHoursEnabled: false,
    blockHoursStart: "12:00",
    blockHoursEnd: "13:00",
};

const toEditableUser = (user) => ({
    ...user,
    password: "",
    external: user.external || false,
    externalFullAccess: user.externalFullAccess || false,
    isMachine: user.isMachine || false,
    serviceHoursEnabled: user.serviceHoursEnabled || false,
    serviceHoursStart: user.serviceHoursStart || "07:00",
    serviceHoursEnd: user.serviceHoursEnd || "13:00",
    blockHoursEnabled: user.blockHoursEnabled || false,
    blockHoursStart: user.blockHoursStart || "12:00",
    blockHoursEnd: user.blockHoursEnd || "13:00",
});

const buildUserUpdatePayload = (user, overrides = {}) => ({
    username: user.username,
    role: parseInt(user.role, 10),
    external: user.external || false,
    externalFullAccess: user.externalFullAccess || false,
    isMachine: user.isMachine || false,
    telegramChatId: user.telegramChatId || "",
    phoneNumber: user.phoneNumber || "",
    serviceHoursEnabled: user.serviceHoursEnabled || false,
    serviceHoursStart: user.serviceHoursEnabled ? (user.serviceHoursStart || null) : null,
    serviceHoursEnd: user.serviceHoursEnabled ? (user.serviceHoursEnd || null) : null,
    blockHoursEnabled: user.blockHoursEnabled || false,
    blockHoursStart: user.blockHoursEnabled ? (user.blockHoursStart || null) : null,
    blockHoursEnd: user.blockHoursEnabled ? (user.blockHoursEnd || null) : null,
    ...overrides,
});

const setServiceMode = (setter, checked) => {
    setter((prev) => ({
        ...prev,
        serviceHoursEnabled: checked,
        blockHoursEnabled: checked ? false : prev.blockHoursEnabled,
    }));
};

const setBlockMode = (setter, checked) => {
    setter((prev) => ({
        ...prev,
        blockHoursEnabled: checked,
        serviceHoursEnabled: checked ? false : prev.serviceHoursEnabled,
    }));
};

const AuditHoursHelp = () => (
    <Alert status="info" mt={4} borderRadius="md" alignItems="flex-start">
        <AlertIcon mt={1} />
        <Box>
            <AlertTitle fontSize="sm">Configuração de Auditoria por Horário</AlertTitle>
            <AlertDescription fontSize="sm" mt={1}>
                Horário de Serviço: Define o horário em que o usuário poderá realizar auditoria de infrações.
            </AlertDescription>
            <AlertDescription fontSize="sm">
                Horário de Bloqueio: Define o horário em que o usuário NÃO poderá realizar auditoria de infrações. Qualquer horário fora desse intervalo fica liberado.
            </AlertDescription>
            <Text fontSize="xs" color="gray.600" mt={2}>
                Apenas um dos dois modos pode ficar ativo por vez. Se ambos estiverem desativados, o usuário fica com horário totalmente liberado.
            </Text>
        </Box>
    </Alert>
);

const UserManagementTab = () => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
    const [newUser, setNewUser] = useState(INITIAL_USER_STATE);
    const [currentUserRole, setCurrentUserRole] = useState(1);
    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const toast = useToast();

    useEffect(() => {
        fetchUsers();
        fetchCurrentUser();
    }, []);

    const fetchCurrentUser = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch("/api/users/me", {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error("Erro ao buscar usuário atual");
            const data = await response.json();
            setCurrentUserRole(data.actualRole ?? data.role);
        } catch (error) {
            console.error("Erro:", error);
        }
    };

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch("/api/users", {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error("Erro ao buscar usuários");
            const data = await response.json();
            setUsers(data);
        } catch (error) {
            console.error("Erro:", error);
            toast({
                title: "Erro ao carregar usuários",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleNewUserInputChange = (e) => {
        const { name, value } = e.target;
        setNewUser((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSelectedUserInputChange = (e) => {
        const { name, value } = e.target;
        setSelectedUser((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleAddUser = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch("/api/users/register", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    ...newUser,
                    serviceHoursStart: newUser.serviceHoursEnabled ? newUser.serviceHoursStart : null,
                    serviceHoursEnd: newUser.serviceHoursEnabled ? newUser.serviceHoursEnd : null,
                }),
            });
            if (!response.ok) throw new Error("Erro ao adicionar usuário");
            fetchUsers();
            onClose();
            setNewUser(INITIAL_USER_STATE);

            toast({
                title: "Usuário adicionado com sucesso",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error("Erro:", error);
            toast({
                title: "Erro ao adicionar usuário",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleUpdateUser = async () => {
        try {
            const token = localStorage.getItem("token");
            const updateData = buildUserUpdatePayload(selectedUser, {
                external: selectedUser.external || false,
                externalFullAccess: selectedUser.externalFullAccess || false,
            });

            if (selectedUser.password && selectedUser.password.trim() !== "") {
                updateData.password = selectedUser.password;
            }

            const response = await fetch(`/api/users/${selectedUser.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(updateData),
            });
            if (!response.ok) throw new Error("Erro ao atualizar usuário");
            fetchUsers();
            onEditClose();

            toast({
                title: "Usuário atualizado com sucesso",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error("Erro:", error);
            toast({
                title: "Erro ao atualizar usuário",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm("Tem certeza que deseja excluir este usuário?")) return;
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`/api/users/${userId}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error("Erro ao deletar usuário");
            fetchUsers();
            toast({
                title: "Usuário deletado com sucesso",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error("Erro:", error);
            toast({
                title: "Erro ao deletar usuário",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleToggleExternalBlock = async (user) => {
        const nextBlocked = !user.external;
        const actionLabel = nextBlocked ? "bloquear" : "liberar";
        if (!window.confirm(`Deseja ${actionLabel} o acesso externo de ${user.username}?`)) return;

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`/api/users/${user.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(buildUserUpdatePayload(user, {
                    external: nextBlocked,
                    externalFullAccess: nextBlocked ? false : (user.externalFullAccess || false),
                })),
            });
            if (!response.ok) throw new Error("Erro ao atualizar acesso externo");
            fetchUsers();
            toast({
                title: nextBlocked ? "Acesso externo bloqueado" : "Acesso externo liberado",
                description: nextBlocked
                    ? `${user.username} só poderá entrar pela rede interna`
                    : `${user.username} poderá acessar de fora da rede (com poderes reduzidos se não for admin)`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error("Erro:", error);
            toast({
                title: "Erro ao alterar acesso externo",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleToggleExternalFullAccess = async (user) => {
        if (Number(user.role) >= 5) {
            toast({
                title: "Administradores já possuem acesso completo",
                status: "info",
                duration: 2500,
                isClosable: true,
            });
            return;
        }

        const nextFullAccess = !user.externalFullAccess;
        const actionLabel = nextFullAccess ? "ceder acesso normal externo" : "revogar acesso normal externo";
        if (!window.confirm(`Deseja ${actionLabel} para ${user.username}?`)) return;

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`/api/users/${user.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(buildUserUpdatePayload(user, {
                    externalFullAccess: nextFullAccess,
                    external: nextFullAccess ? false : (user.external || false),
                })),
            });
            if (!response.ok) throw new Error("Erro ao atualizar acesso normal externo");
            fetchUsers();
            toast({
                title: nextFullAccess ? "Acesso normal cedido" : "Acesso normal revogado",
                description: nextFullAccess
                    ? `${user.username} manterá os poderes do nível ${user.role} mesmo fora da rede`
                    : `${user.username} voltará ao modo restrito fora da rede`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error("Erro:", error);
            toast({
                title: "Erro ao alterar acesso normal externo",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleInitiatePasswordReset = async (userId, username) => {
        if (!window.confirm(`Tem certeza que deseja solicitar redefinição de senha para ${username}?`)) return;
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`/api/users/${userId}/initiate-password-reset`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error("Erro ao iniciar reset de senha");
            toast({
                title: "Reset de senha iniciado",
                description: `${username} precisará redefinir a senha no próximo login`,
                status: "success",
                duration: 5000,
                isClosable: true,
            });
        } catch (error) {
            console.error("Erro:", error);
            toast({
                title: "Erro ao iniciar reset de senha",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const filteredUsers = useMemo(() => {
        const term = search.trim().toLowerCase();
        return users.filter((user) => {
            if (roleFilter && String(user.role) !== String(roleFilter)) return false;
            if (term && !user.username.toLowerCase().includes(term)) return false;
            return true;
        });
    }, [users, search, roleFilter]);

    return (
        <>
            <Flex justify="flex-start" align="center" mb={4} wrap="wrap" gap={2}>
                <Flex bg="white" p={3} borderRadius="md" boxShadow="sm" gap={3} wrap="wrap" align="center">
                    <InputGroup maxW="280px">
                        <InputLeftElement pointerEvents="none">
                            <SearchIcon color="gray.400" />
                        </InputLeftElement>
                        <Input
                            placeholder="Buscar por usuário"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </InputGroup>
                    <Select
                        maxW="220px"
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                    >
                        <option value="">Todos os níveis</option>
                        <option value="1">Nível 1 - Visualização</option>
                        <option value="2">Nível 2 - Lotes/Passagens</option>
                        <option value="3">Nível 3 - Validação</option>
                        <option value="4">Nível 4 - Supervisão</option>
                        <option value="5">Nível 5 - Administrador</option>
                    </Select>
                    <Text fontSize="sm" color="gray.500" ml="auto">
                        {filteredUsers.length} de {users.length}
                    </Text>
                </Flex>
                
                <Button onClick={onOpen} colorScheme="teal" leftIcon={<AddIcon />}>
                    Adicionar Usuário
                </Button>
            </Flex>

            <Accordion allowToggle mb={6} bg="white" borderRadius="md" boxShadow="sm">
                <AccordionItem border="none">
                    <AccordionButton px={4} py={3}>
                        <HStack flex="1" textAlign="left" spacing={2}>
                            <InfoOutlineIcon color="blue.500" />
                            <Text fontWeight="medium">Níveis de acesso e permissões</Text>
                        </HStack>
                        <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel px={4} pb={4}>
                        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={3}>
                            {Object.entries(ROLE_META).map(([level, meta]) => (
                                <Box key={level} p={3} borderWidth="1px" borderColor="gray.200" borderRadius="md">
                                    <HStack mb={1} spacing={2}>
                                        <Badge colorScheme={meta.color}>Nível {level}</Badge>
                                        <Text fontWeight="semibold" fontSize="sm">{meta.label}</Text>
                                    </HStack>
                                    <Badge colorScheme={meta.networkColor} fontSize="2xs">{meta.network}</Badge>
                                </Box>
                            ))}
                        </SimpleGrid>
                        <Text fontSize="xs" color="gray.500" mt={3}>
                            Todos podem acessar de fora da rede. Fora da rede, níveis 1–4 ficam com poderes de visualização
                            (passagens + ver infrações, sem validar e sem lotes). Nível 5 mantém poderes completos.
                            Use &quot;Bloquear acesso externo&quot; para impedir login fora da rede, ou &quot;Ceder acesso normal&quot;
                            para manter os poderes do nível mesmo fora da rede. Contas de Máquina não usam o painel web e
                            acessam apenas as rotas do Fiscalizer-Agent.
                        </Text>
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>

            <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)", xl: "repeat(4, 1fr)" }}
                gap={4}
            >
                {filteredUsers.map((user) => {
                    const isMachineUser = Boolean(user.isMachine);
                    const meta = isMachineUser
                        ? { label: "Conta de Máquina", color: "gray" }
                        : (ROLE_META[user.role] || { label: "Desconhecido", color: "gray" });
                    const lastLoginRelative = formatRelative(user.lastLoginAt);
                    const lastAccessRelative = formatRelative(user.lastAccessAt);

                    return (
                        <GridItem key={user.id}>
                            <Box
                                bg="white"
                                boxShadow="sm"
                                borderRadius="md"
                                borderWidth="1px"
                                borderColor="gray.200"
                                p={4}
                                h="100%"
                                display="flex"
                                flexDirection="column"
                                transition="box-shadow 0.15s"
                                _hover={{ boxShadow: "md" }}
                            >
                                <Flex justify="space-between" align="flex-start" mb={2} gap={2}>
                                    <Box minW={0}>
                                        <Text fontSize="md" fontWeight="bold" isTruncated title={user.username}>
                                            {user.username}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500" isTruncated>
                                            {meta.label}
                                        </Text>
                                    </Box>
                                    {isMachineUser ? (
                                        <Badge colorScheme="gray" fontSize="xs" flexShrink={0}>
                                            Máquina
                                        </Badge>
                                    ) : (
                                        <Badge colorScheme={meta.color} fontSize="xs" flexShrink={0}>
                                            Nível {user.role}
                                        </Badge>
                                    )}
                                </Flex>

                                {(user.external || user.externalFullAccess || user.isMachine || user.serviceHoursEnabled || user.blockHoursEnabled) && (
                                    <HStack spacing={1} flexWrap="wrap" mb={3}>
                                        {user.external && (
                                            <Badge colorScheme="red" fontSize="2xs" variant="subtle">Externo bloqueado</Badge>
                                        )}
                                        {user.externalFullAccess && (
                                            <Badge colorScheme="purple" fontSize="2xs" variant="subtle">Acesso normal externo</Badge>
                                        )}
                                        {user.isMachine && (
                                            <Badge colorScheme="gray" fontSize="2xs" variant="subtle">Máquina</Badge>
                                        )}
                                        {user.serviceHoursEnabled && (
                                            <Badge colorScheme="blue" fontSize="2xs" variant="subtle">
                                                Serviço {user.serviceHoursStart}–{user.serviceHoursEnd}
                                            </Badge>
                                        )}
                                        {user.blockHoursEnabled && (
                                            <Badge colorScheme="orange" fontSize="2xs" variant="subtle">
                                                Bloqueio {user.blockHoursStart}–{user.blockHoursEnd}
                                            </Badge>
                                        )}
                                    </HStack>
                                )}

                                <Divider />

                                <VStack align="stretch" spacing={1.5} fontSize="xs" color="gray.600" py={3}>
                                    <HStack spacing={2}>
                                        <TimeIcon boxSize="11px" color="gray.400" />
                                        <Text minW="110px" color="gray.500">Última entrada:</Text>
                                        <Tooltip label={lastLoginRelative || ""} isDisabled={!lastLoginRelative} openDelay={300}>
                                            <Text fontWeight="medium" color={user.lastLoginAt ? "gray.700" : "gray.400"}>
                                                {formatDateTime(user.lastLoginAt)}
                                            </Text>
                                        </Tooltip>
                                    </HStack>
                                    <HStack spacing={2}>
                                        <RepeatClockIcon boxSize="11px" color="gray.400" />
                                        <Text minW="110px" color="gray.500">Último acesso:</Text>
                                        <Tooltip label={lastAccessRelative || ""} isDisabled={!lastAccessRelative} openDelay={300}>
                                            <Text fontWeight="medium" color={user.lastAccessAt ? "gray.700" : "gray.400"}>
                                                {formatDateTime(user.lastAccessAt)}
                                            </Text>
                                        </Tooltip>
                                    </HStack>
                                </VStack>

                                <Divider />

                                <HStack mt={3} spacing={2} justify="flex-end">
                                    {!isMachineUser && Number(user.role) < 5 && (
                                        <Tooltip label={user.externalFullAccess ? "Revogar acesso normal externo" : "Ceder acesso normal (externo)"}>
                                            <IconButton
                                                size="sm"
                                                variant="ghost"
                                                colorScheme={user.externalFullAccess ? "purple" : "blue"}
                                                aria-label={user.externalFullAccess ? "Revogar acesso normal externo" : "Ceder acesso normal externo"}
                                                icon={<UnlockIcon />}
                                                onClick={() => handleToggleExternalFullAccess(user)}
                                            />
                                        </Tooltip>
                                    )}
                                    {!isMachineUser && (
                                    <Tooltip label={user.external ? "Liberar acesso externo" : "Bloquear acesso externo"}>
                                        <IconButton
                                            size="sm"
                                            variant="ghost"
                                            colorScheme={user.external ? "green" : "red"}
                                            aria-label={user.external ? "Liberar acesso externo" : "Bloquear acesso externo"}
                                            icon={user.external ? <CheckIcon /> : <NotAllowedIcon />}
                                            onClick={() => handleToggleExternalBlock(user)}
                                        />
                                    </Tooltip>
                                    )}
                                    <Tooltip label="Editar">
                                        <IconButton
                                            size="sm"
                                            variant="ghost"
                                            colorScheme="teal"
                                            aria-label="Editar"
                                            icon={<EditIcon />}
                                            onClick={() => {
                                                setSelectedUser(toEditableUser(user));
                                                onEditOpen();
                                            }}
                                        />
                                    </Tooltip>
                                    {currentUserRole >= 4 && (
                                        <Tooltip label="Redefinir senha">
                                            <IconButton
                                                size="sm"
                                                variant="ghost"
                                                colorScheme="orange"
                                                aria-label="Redefinir senha"
                                                icon={<LockIcon />}
                                                onClick={() => handleInitiatePasswordReset(user.id, user.username)}
                                            />
                                        </Tooltip>
                                    )}
                                    <Tooltip label="Excluir">
                                        <IconButton
                                            size="sm"
                                            variant="ghost"
                                            colorScheme="red"
                                            aria-label="Excluir"
                                            icon={<DeleteIcon />}
                                            onClick={() => handleDeleteUser(user.id)}
                                        />
                                    </Tooltip>
                                </HStack>
                            </Box>
                        </GridItem>
                    );
                })}
                {filteredUsers.length === 0 && (
                    <GridItem colSpan={{ base: 1, md: 2, lg: 3, xl: 4 }}>
                        <Box bg="white" p={8} borderRadius="md" textAlign="center" color="gray.500">
                            Nenhum usuário encontrado para o filtro atual.
                        </Box>
                    </GridItem>
                )}
            </Grid>

            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Adicionar Novo Usuário</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Input
                            placeholder="Nome de usuário"
                            name="username"
                            value={newUser.username}
                            onChange={handleNewUserInputChange}
                            mb={4}
                        />
                        <Input
                            placeholder="Senha"
                            name="password"
                            type="password"
                            value={newUser.password}
                            onChange={handleNewUserInputChange}
                            mb={4}
                        />
                        <Select
                            name="role"
                            value={newUser.role}
                            onChange={handleNewUserInputChange}
                            mb={4}
                        >
                            <option value={1}>Nível 1 - Visualização de Passagens</option>
                            <option value={2}>Nível 2 - Lotes e Passagens</option>
                            <option value={3}>Nível 3 - Validação</option>
                            <option value={4}>Nível 4 - Supervisão</option>
                            <option value={5}>Nível 5 - Administrador</option>
                        </Select>
                        <FormControl>
                            <Checkbox
                                isChecked={newUser.external}
                                onChange={(e) => setNewUser((prev) => ({
                                    ...prev,
                                    external: e.target.checked,
                                    externalFullAccess: e.target.checked ? false : prev.externalFullAccess,
                                }))}
                            >
                                <FormLabel mb={0}>Bloquear acesso externo</FormLabel>
                            </Checkbox>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Impede que o usuário acesse o sistema fora da rede interna
                            </Text>
                        </FormControl>
                        <FormControl mt={4}>
                            <Checkbox
                                isChecked={newUser.externalFullAccess}
                                isDisabled={Number(newUser.role) >= 5}
                                onChange={(e) => setNewUser((prev) => ({
                                    ...prev,
                                    externalFullAccess: e.target.checked,
                                    external: e.target.checked ? false : prev.external,
                                }))}
                            >
                                <FormLabel mb={0}>Ceder acesso normal (externo)</FormLabel>
                            </Checkbox>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Mantém os poderes do nível do usuário mesmo fora da rede interna
                            </Text>
                        </FormControl>
                        <FormControl mt={4}>
                            <Checkbox
                                isChecked={newUser.isMachine}
                                onChange={(e) => setNewUser((prev) => ({ ...prev, isMachine: e.target.checked }))}
                            >
                                <FormLabel mb={0}>Conta de Máquina</FormLabel>
                            </Checkbox>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Credenciais para o Fiscalizer-Agent: sem painel web, acesso às rotas de máquina/câmeras/ROI/infrações/passagens. Logins não entram nos logs de acesso.
                            </Text>
                        </FormControl>
                        <FormControl mt={4}>
                            <Checkbox
                                isChecked={newUser.serviceHoursEnabled}
                                onChange={(e) => setServiceMode(setNewUser, e.target.checked)}
                            >
                                <FormLabel mb={0}>Definir Horário de Serviço</FormLabel>
                            </Checkbox>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Libera a auditoria apenas dentro do intervalo definido (Horário de Brasília)
                            </Text>
                            {newUser.serviceHoursEnabled && (
                                <HStack mt={2}>
                                    <Box flex={1}>
                                        <FormLabel fontSize="sm" mb={1}>Das</FormLabel>
                                        <Input
                                            type="time"
                                            value={newUser.serviceHoursStart}
                                            onChange={(e) => setNewUser((prev) => ({ ...prev, serviceHoursStart: e.target.value }))}
                                        />
                                    </Box>
                                    <Box flex={1}>
                                        <FormLabel fontSize="sm" mb={1}>Às</FormLabel>
                                        <Input
                                            type="time"
                                            value={newUser.serviceHoursEnd}
                                            onChange={(e) => setNewUser((prev) => ({ ...prev, serviceHoursEnd: e.target.value }))}
                                        />
                                    </Box>
                                </HStack>
                            )}
                        </FormControl>
                        <FormControl mt={4}>
                            <Checkbox
                                isChecked={newUser.blockHoursEnabled}
                                onChange={(e) => setBlockMode(setNewUser, e.target.checked)}
                            >
                                <FormLabel mb={0}>Definir Horário de Bloqueio</FormLabel>
                            </Checkbox>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Bloqueia a auditoria apenas dentro do intervalo definido. Fora dele, o acesso fica liberado.
                            </Text>
                            {newUser.blockHoursEnabled && (
                                <HStack mt={2}>
                                    <Box flex={1}>
                                        <FormLabel fontSize="sm" mb={1}>Das</FormLabel>
                                        <Input
                                            type="time"
                                            value={newUser.blockHoursStart}
                                            onChange={(e) => setNewUser((prev) => ({ ...prev, blockHoursStart: e.target.value }))}
                                        />
                                    </Box>
                                    <Box flex={1}>
                                        <FormLabel fontSize="sm" mb={1}>Às</FormLabel>
                                        <Input
                                            type="time"
                                            value={newUser.blockHoursEnd}
                                            onChange={(e) => setNewUser((prev) => ({ ...prev, blockHoursEnd: e.target.value }))}
                                        />
                                    </Box>
                                </HStack>
                            )}
                        </FormControl>
                        <AuditHoursHelp />
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="teal" onClick={handleAddUser}>Adicionar</Button>
                        <Button onClick={onClose} ml={3}>Cancelar</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={isEditOpen} onClose={onEditClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Editar Usuário</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Input
                            placeholder="Nome de usuário"
                            name="username"
                            value={selectedUser?.username || ""}
                            onChange={handleSelectedUserInputChange}
                            mb={4}
                        />
                        <Input
                            placeholder="Nova senha (opcional)"
                            name="password"
                            type="password"
                            onChange={handleSelectedUserInputChange}
                            mb={4}
                        />
                        <Select
                            name="role"
                            value={selectedUser?.role || 1}
                            onChange={handleSelectedUserInputChange}
                            mb={4}
                        >
                            <option value={1}>Nível 1 - Visualização de Passagens</option>
                            <option value={2}>Nível 2 - Lotes e Passagens</option>
                            <option value={3}>Nível 3 - Validação</option>
                            <option value={4}>Nível 4 - Supervisão</option>
                            <option value={5}>Nível 5 - Administrador</option>
                        </Select>
                        <FormControl>
                            <Checkbox
                                isChecked={selectedUser?.external || false}
                                onChange={(e) => setSelectedUser((prev) => ({
                                    ...prev,
                                    external: e.target.checked,
                                    externalFullAccess: e.target.checked ? false : prev.externalFullAccess,
                                }))}
                            >
                                <FormLabel mb={0}>Bloquear acesso externo</FormLabel>
                            </Checkbox>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Impede que o usuário acesse o sistema fora da rede interna
                            </Text>
                        </FormControl>
                        <FormControl mt={4}>
                            <Checkbox
                                isChecked={selectedUser?.externalFullAccess || false}
                                isDisabled={Number(selectedUser?.role) >= 5}
                                onChange={(e) => setSelectedUser((prev) => ({
                                    ...prev,
                                    externalFullAccess: e.target.checked,
                                    external: e.target.checked ? false : prev.external,
                                }))}
                            >
                                <FormLabel mb={0}>Ceder acesso normal (externo)</FormLabel>
                            </Checkbox>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Mantém os poderes do nível do usuário mesmo fora da rede interna
                            </Text>
                        </FormControl>
                        <FormControl mt={4}>
                            <Checkbox
                                isChecked={selectedUser?.isMachine || false}
                                onChange={(e) => setSelectedUser((prev) => ({ ...prev, isMachine: e.target.checked }))}
                            >
                                <FormLabel mb={0}>Conta de Máquina</FormLabel>
                            </Checkbox>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Credenciais para o Fiscalizer-Agent: sem painel web, acesso às rotas de máquina/câmeras/ROI/infrações/passagens. Logins não entram nos logs de acesso.
                            </Text>
                        </FormControl>
                        <FormControl mt={4}>
                            <Checkbox
                                isChecked={selectedUser?.serviceHoursEnabled || false}
                                onChange={(e) => setServiceMode(setSelectedUser, e.target.checked)}
                            >
                                <FormLabel mb={0}>Definir Horário de Serviço</FormLabel>
                            </Checkbox>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Libera a auditoria apenas dentro do intervalo definido (Horário de Brasília)
                            </Text>
                            {selectedUser?.serviceHoursEnabled && (
                                <HStack mt={2}>
                                    <Box flex={1}>
                                        <FormLabel fontSize="sm" mb={1}>Das</FormLabel>
                                        <Input
                                            type="time"
                                            value={selectedUser?.serviceHoursStart || "07:00"}
                                            onChange={(e) => setSelectedUser((prev) => ({ ...prev, serviceHoursStart: e.target.value }))}
                                        />
                                    </Box>
                                    <Box flex={1}>
                                        <FormLabel fontSize="sm" mb={1}>Às</FormLabel>
                                        <Input
                                            type="time"
                                            value={selectedUser?.serviceHoursEnd || "13:00"}
                                            onChange={(e) => setSelectedUser((prev) => ({ ...prev, serviceHoursEnd: e.target.value }))}
                                        />
                                    </Box>
                                </HStack>
                            )}
                        </FormControl>
                        <FormControl mt={4}>
                            <Checkbox
                                isChecked={selectedUser?.blockHoursEnabled || false}
                                onChange={(e) => setBlockMode(setSelectedUser, e.target.checked)}
                            >
                                <FormLabel mb={0}>Definir Horário de Bloqueio</FormLabel>
                            </Checkbox>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Bloqueia a auditoria apenas dentro do intervalo definido. Fora dele, o acesso fica liberado.
                            </Text>
                            {selectedUser?.blockHoursEnabled && (
                                <HStack mt={2}>
                                    <Box flex={1}>
                                        <FormLabel fontSize="sm" mb={1}>Das</FormLabel>
                                        <Input
                                            type="time"
                                            value={selectedUser?.blockHoursStart || "12:00"}
                                            onChange={(e) => setSelectedUser((prev) => ({ ...prev, blockHoursStart: e.target.value }))}
                                        />
                                    </Box>
                                    <Box flex={1}>
                                        <FormLabel fontSize="sm" mb={1}>Às</FormLabel>
                                        <Input
                                            type="time"
                                            value={selectedUser?.blockHoursEnd || "13:00"}
                                            onChange={(e) => setSelectedUser((prev) => ({ ...prev, blockHoursEnd: e.target.value }))}
                                        />
                                    </Box>
                                </HStack>
                            )}
                        </FormControl>
                        <AuditHoursHelp />
                        <Input
                            placeholder="Telegram Chat ID (opcional)"
                            name="telegramChatId"
                            value={selectedUser?.telegramChatId || ""}
                            onChange={handleSelectedUserInputChange}
                            mt={4}
                        />
                        <Text fontSize="xs" color="gray.500" mt={1} mb={4}>
                            ID do chat no Telegram para receber notificações diretas. Use @userinfobot para descobrir.
                        </Text>
                        <Input
                            placeholder="WhatsApp (ex: 5511999998888)"
                            name="phoneNumber"
                            value={selectedUser?.phoneNumber || ""}
                            onChange={handleSelectedUserInputChange}
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                            Número no formato internacional sem '+'. Usado para notificações via WhatsApp.
                        </Text>
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="teal" onClick={handleUpdateUser}>Salvar Alterações</Button>
                        <Button onClick={onEditClose} ml={3}>Cancelar</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default UserManagementTab;